import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import { addNoDiffAnalytics } from '../ui/utils/analytics';
import { applyToolCalls } from '../ui/utils/toolCalls/applyToolCalls';

import type { SelectSuggestionAction } from './types';

/**
 * Returns the position of the first node in `doc` whose `localId` attr matches
 * one of `suggestion`'s tool call argument localIds. Tool calls are checked in
 * order; for `removeNodes` we walk its `localIds` array in order too.
 *
 * Used as a scroll target when applying the suggestion produces no diff: the
 * show-diff plugin's `scrollIntoView` is a no-op without any diff decorations,
 * so without this the card stays anchored to its tool call localId but the
 * viewport never moves, leaving the card pinned to the viewport edge instead
 * of sitting next to its target node.
 */
function findFirstToolCallNodePosition(doc: PMNode, suggestion: Suggestion): number | undefined {
	const findLocalIdPos = (localId: string): number | undefined => {
		let result: number | undefined;
		doc.descendants((node, pos) => {
			if (result !== undefined) {
				return false;
			}
			if (node.attrs?.localId === localId) {
				result = pos;
				return false;
			}
			return true;
		});
		return result;
	};

	for (const toolCall of suggestion.toolCalls) {
		const localIds =
			toolCall.name === 'removeNodes' ? toolCall.arguments.localIds : [toolCall.arguments.localId];
		for (const localId of localIds) {
			const pos = findLocalIdPos(localId);
			if (pos !== undefined) {
				return pos;
			}
		}
	}
	return undefined;
}

/**
 * Open every `expand` / `nestedExpand` ancestor of `pos` so the card's
 * `anchor-name` spans (emitted at the suggestion target) become layout-
 * visible instead of being hidden under the expand's collapsed content
 * wrapper, where CSS anchor positioning can't resolve them.
 *
 * Delegates to `toggleExpandRange` — it writes `expandedState` (the
 * expand's source of truth) *and* sets meta the expand plugin reads to
 * emit `Decoration.node({ forceExpandOpen })`, which is the outer-deco
 * change that makes PM call `ExpandNodeView.update()`. The range starts
 * at the top-level ancestor so `nodesBetween` definitely visits every
 * wrapping expand.
 */
function revealAncestorExpands(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	doc: PMNode,
	pos: number,
	tr: Transaction,
): void {
	const $pos = doc.resolve(pos);
	if ($pos.depth === 0) {
		return;
	}

	let hasExpandAncestor = false;
	for (let depth = $pos.depth; depth > 0; depth--) {
		const node = $pos.node(depth);
		if (node.type.name === 'expand' || node.type.name === 'nestedExpand') {
			hasExpandAncestor = true;
			break;
		}
	}
	if (!hasExpandAncestor) {
		return;
	}

	api?.expand?.commands.toggleExpandRange($pos.before(1), pos, true)?.({ tr });
}

export const selectSuggestionAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => SelectSuggestionAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) =>
	(id: string | null, options?: { hideDeletedDiffs?: boolean }): boolean => {
		// Captured during the `execute` callback so we can run the scroll after the
		// transaction has been dispatched — that way `SELECT_SUGGESTION` has already
		// flowed through the plugin state and the card anchors are in the DOM by
		// the time the viewport moves into place.
		let noDiffScrollPos: number | undefined;

		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, { action: 'SELECT_SUGGESTION', id: id ?? undefined });

			if (id === null) {
				// Deselecting — hide the diff
				api?.showDiff?.commands.hideDiff?.({ tr });
			} else {
				// Selecting — show the diff for this suggestion
				const suggestion = api?.aiSuggestions?.sharedState.currentState()?.suggestionById.get(id);
				if (suggestion) {
					const firstToolCallPos = findFirstToolCallNodePosition(tr.doc, suggestion);

					if (firstToolCallPos !== undefined) {
						revealAncestorExpands(api, tr.doc, firstToolCallPos, tr);
					}

					// Apply tool calls to an isolated transaction so we can read its `steps`
					// and `before` for diff visualisation without affecting the source transaction.
					const isolatedTr = new Transform(tr.doc);
					const appliedTr = applyToolCalls({ tr: isolatedTr, suggestion });

					if (appliedTr.before.eq(appliedTr.doc)) {
						//fire analytics if there is no diff
						addNoDiffAnalytics(api?.analytics?.actions)(tr, suggestion);
						// No diff → showDiff's `scrollIntoView` won't scroll (no decorations).
						// Stash the first tool call localId position so we can scroll the
						// viewport to it ourselves after dispatch.
						noDiffScrollPos = firstToolCallPos;
					}

					api?.showDiff?.commands.showDiff({
						// For a multi-step transaction, each step's `from`/`to` are
						// in the coordinate space of the doc BEFORE that specific
						// step is applied — i.e. `appliedTr.docs[i]`, not the
						// initial `appliedTr.before`. Inverting every step against
						// `appliedTr.before` throws `RangeError` (or silently
						// produces broken inverted steps) for any step past index 0.
						// Reverse the inverted-step array so they apply back to the
						// original order when the diff plugin replays them on
						// `appliedTr.doc`.
						steps: appliedTr.steps.map((step, i) => step.invert(appliedTr.docs[i])).reverse(),
						originalDoc: appliedTr.doc,
						isInverted: true,
						diffType: 'step',
						hideDeletedDiffs: options?.hideDeletedDiffs,
						scrollIntoView: true,
					})({ tr });
				}
			}

			return tr;
		});

		if (noDiffScrollPos !== undefined) {
			api?.core?.actions.scrollToPos(noDiffScrollPos, { behavior: 'smooth', block: 'start' });
		}

		return true;
	};
